import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {NgFor, NgIf, NgPlural, NgPluralCase} from '@angular/common';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Activity} from '../../interfaces/activity.interface';
import {ActivityDisplayService} from './activity-display.service';
import {take} from 'rxjs';
import {AccordionComponent, ButtonModule, NavigationService} from '@adnova/jf-ng-components';
import {Store} from '@ngrx/store';
import {ActivityDisplayActions} from './+store/activity-display.actions';
import {ActivityDisplaySelectors} from './+store/activity-display.selectors';
import {activityDisplayInitialState} from './+store/activity-display.initial.state';
import {AppState} from '../../store/states/app.state';
import {ActivityComponent} from './activity/activity.component';

@Component({
  selector: 'app-activity-display',
  templateUrl: './activity-display.component.html',
  styleUrls: ['./activity-display.component.scss'],
  standalone: true,
  imports: [NgIf, NgFor, NgPlural, NgPluralCase, AccordionComponent, ButtonModule, ActivityComponent],
})
export class ActivityDisplayComponent implements OnInit {
  private readonly _destroyRef = inject(DestroyRef);

  // INFO: Die Id des ausgewählten Inhabers.
  private currentInhaberId = '';

  // INFO: Die Liste der Aktivitäten
  public activities: Activity[] = [];

  // INFO: Das Accordion soll initial immer geschlossen sein.
  public isExpansionpanelOpen = activityDisplayInitialState.isOpen;

  // INFO: Anzahl der laufenden Aktivitäten
  public activitiesOngoingCount = 0;

  // INFO: Das Accordion soll initial immer ausgeblendet sein.
  public showActivityDisplay = false;

  // INFO: Die Anzeige des Accordion soll initial immer erlaubt sein.
  public neverShowActivityDisplay = false;

  constructor(
    private navigationService: NavigationService,
    private activityDisplayService: ActivityDisplayService,
    private _store: Store<AppState>,
  ) {}

  ngOnInit() {
    this._store
      .select(ActivityDisplaySelectors.isOpen)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(isOpen => {
        this.isExpansionpanelOpen = isOpen;
      });

    this.navigationService.currentInhaber$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(inhaber => {
      if (!inhaber) return;
      const inhaberId = inhaber.id;

      this.currentInhaberId = inhaberId;

      this.activityDisplayService
        .getActivities(inhaberId)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe(activities => {
          if (this.currentInhaberId === inhaberId) {
            // INFO: Zählen der laufenden Aktivitäten
            let activitiesOngoingCount = 0;
            for (const activity of activities) {
              if (!activity.done) {
                activitiesOngoingCount += 1;
              }
            }

            // INFO: Wenn die Anzahl der Aktivitäten sich geändert hat, soll das Accordion geöffnet werden ...
            const differentActivities = this.activities.length !== activities.length;

            // INFO: ... außer wenn die letzte Aktivität eine Lösch-Aktivität ist.
            const lastIsNotDeleting = activities.length > 0 && !activities[0].title.toLowerCase().includes('löschen');

            if (differentActivities && lastIsNotDeleting) {
              this._store.dispatch(ActivityDisplayActions.open());
            }

            this.activitiesOngoingCount = activitiesOngoingCount;
            // INFO: Aktualisieren der Aktivitäten.
            this.activities = activities;
          }
        });

      // INFO: Ermitteln, ob die Aktivitäten-Anzeige bei dem Inhaber dargestellt werden soll.
      this.activityDisplayService
        .showActivityDisplayByInhaber(this.currentInhaberId)
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe(show => {
          this.showActivityDisplay = show;
        });
    });

    // INFO: Ermitteln, ob die Aktivitäten-Anzeige grundsätzlich angezeigt werden darf.
    this.activityDisplayService.neverShowActivityDisplay
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(neverShow => {
        this.neverShowActivityDisplay = neverShow;
      });
  }

  /**
   * Setzen des State des Accordion.
   * @param isOpen Offen oder nicht.
   */
  changeExpansionpanelState(isOpen: boolean): void {
    if (isOpen) {
      this._store.dispatch(ActivityDisplayActions.open());
    } else {
      this._store.dispatch(ActivityDisplayActions.close());
    }
  }

  /**
   * Triggert das Ausblenden der Aktivitäten-Anzeige.
   */
  hideActivityDisplay(): void {
    this.activityDisplayService.activityDisplayShouldHide(this.currentInhaberId);
  }
}
